/**
 * This function checks if the input contains a URL
 * Example: http://example.com, https://example.com, www.example.com, example.com will all return true
 * @param input
 * @returns boolean
 */
export function containsUrl(input: unknown): boolean {
  if (typeof input !== 'string') {
    return false
  }

  // Regex breakdown:
  // \b                            : Word boundary for URL matching
  // (                             : Main URL group start
  //   (https?:\/\/|www\.)         : Protocol or www prefix
  //   [^\s]+                      : Domain content after prefix
  //   |                           : OR
  //   [\p{L}0-9-]+\.[a-zA-Z]{2,}  : Domain pattern (e.g., example.com)
  // )                             : Main URL group end
  // (\/\S*)?                      : Optional path component
  // \b                            : Word boundary
  // gu                            : Global and Unicode flags
  const urlRegex = /\b((https?:\/\/|www\.)[^\s]+|[\p{L}0-9-]+\.[a-zA-Z]{2,})(\/\S*)?\b/gu

  return urlRegex.test(input)
}

/**
 * This function checks if the input contains only safe characters
 * Safe characters are defined as letters, numbers, and certain punctuation marks.
 * @param input
 * @returns boolean
 */
export function containsOnlySafeCharacters(input: unknown): boolean {
  if (typeof input !== 'string') {
    return false
  }

  // Regex breakdown:
  // ^               : Start of string
  // [               : Start of character class
  //   \p{L}         : Any Unicode letter (all languages)
  //   \p{N}         : Any Unicode digit
  //   \s            : Whitespace characters
  //   \-            : Hyphen (escaped)
  //   ',            : Apostrophe and comma
  //   .             : Period
  // ]*              : Zero or more of above chars
  // $               : End of string
  // u               : Unicode flag
  const safePattern = /^[\p{L}\p{N}\s\-',.]*$/u
  return safePattern.test(input)
}

/**
 * Processes rule options in boolean|string format
 * @param option - The rule option (true for default message, string for custom message)
 * @param defaultMessage - Default message to use if option is true
 */
const processRuleOption = (
  option?: boolean | string,
  defaultMessage?: string
): string | undefined =>
  typeof option === 'string' ? option : option === true ? defaultMessage : undefined

/**
 * Returns validation rules for text input fields, with configurable URL and character validations.
 *
 * @param defaultMessage - Default error message used when specific messages aren't provided
 * @param options - Configuration options for validations.
 * For the boolean | string options types, if you pass true, it will be enabled and the default message will be used and if you pass a string, it will also be enabled and be used as the message.
 *
 * @returns Object containing validation rules for React Hook Form
 */
export const getValidationRulesForTextField = (
  defaultMessage: string,
  options?: {
    noUrls?: boolean | string
    safeChars?: boolean | string
    required?: boolean | string
    maxLength?: { value: number; message: string }
  }
) => {
  const requiredRule = processRuleOption(options?.required, defaultMessage)
  const noUrlsRule = processRuleOption(options?.noUrls, defaultMessage)
  const safeCharsRule = processRuleOption(options?.safeChars, defaultMessage)
  return {
    ...(requiredRule !== undefined && { required: requiredRule }),
    ...(options?.maxLength && { maxLength: options.maxLength }),
    validate: {
      ...(noUrlsRule !== undefined && {
        noUrls: (value: unknown) => !value || !containsUrl(value) || noUrlsRule,
      }),
      ...(safeCharsRule !== undefined && {
        safeChars: (value: unknown) => !value || containsOnlySafeCharacters(value) || safeCharsRule,
      }),
    },
  }
}
